import React from 'react';
import PropTypes from 'prop-types';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TextField from '@material-ui/core/TextField';
import Input from '@material-ui/core/Input';
import MenuItem from '@material-ui/core/MenuItem';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import Select from '@material-ui/core/Select';
import Collapse from '@material-ui/core/Collapse';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import Switch from '@material-ui/core/Switch';
import AlarmDescription from './AlarmDescription.js';
import { alarmTypes } from './AlarmItem';
import { alarmStatus } from './AlarmItem';

const units = 'км';

const AlarmDistance = ({
  classes,
  editMode,
  data,
  onChange,
  onDelete,
  onError,
}) => {
  const [open, setOpen] = React.useState(data.value == null);
  const inputValue = React.useRef('');
  const errorMessage =
    'Моля попълнете параметър "Разстояние", преди да активирате алармата';

  const handleError = React.useCallback(
    msg => {
      onError && onError(msg);
    },
    [onError],
  );

  const handleTypeChange = React.useCallback(
    ev => {
      const type = ev.currentTarget.getAttribute('data-alarm-type');
      onChange(data, Object.assign({}, data, { type: type, value: null }));
    },
    [data, onChange],
  );

  const handleNameChange = React.useCallback(
    ev => {
      onChange(data, Object.assign({}, data, { name: ev.target.value }));
    },
    [data, onChange],
  );

  const handleDistanceChange = React.useCallback(
    ev => {
      const props = ev.target.value
        ? { value: ev.target.value }
        : { value: ev.target.value, status: 'finished' };
      onChange(data, Object.assign({}, data, props));
    },
    [data, onChange],
  );

  const handleTextChange = React.useCallback(
    ev => {
      onChange(data, Object.assign({}, data, { text: ev.target.value }));
    },
    [data, onChange],
  );

  const handleDelete = React.useCallback(() => {
    onDelete(data.id);
  }, [data.id, onDelete]);

  const handleStateChange = React.useCallback(
    ev => {
      if (ev.target.checked && !inputValue.current.value.length) {
        handleError(errorMessage);
        return;
      }

      const status = ev.target.checked
        ? alarmStatus.waiting
        : alarmStatus.finished;
      onChange(data, Object.assign({}, data, { status: status }));
    },
    [data, onChange, handleError],
  );

  return (
    <React.Fragment>
      <TableRow>
        <TableCell>
          <IconButton
            aria-label='expand row'
            size='small'
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Input
            classes={{ disabled: classes.inputDisabled }}
            defaultValue={data.name}
            disabled={editMode !== true}
            label={'Име на аларма'}
            name={'alarm-name'}
            inputProps={{ minLength: 2 }}
            disableUnderline={editMode !== true}
            onChange={handleNameChange}
          />
        </TableCell>
        <TableCell>
          <Select
            classes={{ disabled: classes.selectDisabled }}
            label='Вид'
            disabled={editMode !== true}
            value={alarmTypes[1].value}
            onChange={handleTypeChange}
            name={'alarm-type'}
            disableUnderline={editMode !== true}
          >
            {alarmTypes.map(item => (
              <MenuItem
                key={item.id}
                data-alarm-type={item.id}
                value={item.value}
              >
                {item.value}
              </MenuItem>
            ))}
          </Select>
        </TableCell>
        <TableCell>
          <TextField
            defaultValue={data.value}
            disabled={editMode !== true}
            label={'Разстояние'}
            type={'number'}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>{units}</InputAdornment>
              ),
              disableUnderline: editMode !== true,
              classes: { disabled: classes.inputDisabled },
            }}
            inputRef={inputValue}
            name={'alarm-trigger-value'}
            onChange={handleDistanceChange}
          />
        </TableCell>
        <TableCell>
          <Switch
            classes={{ switchBase: classes.switchDisabled }}
            checked={data.status === 'waiting' || data.status === 'soon'}
            onChange={handleStateChange}
            name='alarm-state'
            disabled={editMode !== true}
          />
        </TableCell>
        {editMode && (
          <TableCell>
            <IconButton aria-label='delete' onClick={handleDelete}>
              <DeleteForeverIcon />
            </IconButton>
          </TableCell>
        )}
      </TableRow>
      <TableRow>
        <TableCell
          colSpan={editMode ? 6 : 5}
          className={classes.descriptionCell}
          style={{ padding: 0 }}
        >
          <Collapse in={open} timeout='auto' unmountOnExit>
            <AlarmDescription
              disabled={editMode !== true}
              placeholder={'Въведете описание на алармата'}
              onChange={handleTextChange}
              defaultValue={data.text}
            />
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

AlarmDistance.propTypes = {
  data: PropTypes.object,
  classes: PropTypes.object,
  editMode: PropTypes.bool,
  onChange: PropTypes.func,
  onDelete: PropTypes.func,
  onError: PropTypes.func,
};

export default AlarmDistance;
